<template>
  <div :key="table_key">
    <div v-if="pdf_page">
      <pdf-gen
        :header-data="Array(pdf_header)"
        :page-margins="[15, 110, 15, 60]"
        :footer-data="Array(pdf_footer)"
        :content-data="Array(pdf_content)"
        ref="invoice"
        :name="
          $route.params.id +
          '-' +
          String($route.params.report_id).padStart(5, '0') +
          ' ' +
          data.data.project.name +
          ' ' +
          ' Vk ' +
          selectedDates.weeks
        "
      />
    </div>
    <v-form v-else ref="form" lazy-validation>
      <v-card class="mb-5 mx-auto f12" width="100%" max-width="800" tile>
        <div class="grid_head pa-4">
          <div>
            <v-img
              :src="companiesConfig.logo.png"
              max-height="70"
              max-width="120"
              height="100%"
              width="100%"
              contain
              position="left"
            />
            <div class="mt-2 lh13">{{ companiesConfig.company_name }}</div>
            <div class="lh13">
              {{ companiesConfig.registration_address_street }}, {{ companiesConfig.registration_address_zip }}, {{ companiesConfig.registration_address_city }}
            </div>
          </div>
          <div class="grid">
            <div>
              <div class="f14 fw500 lh13">Lasku</div>
              <div class="fw500 lh13">Päivämäärä</div>
              <div class="lh13">{{ today.dd }}.{{ today.mm }}.{{ today.yyyy }}</div>
            </div>
            <div>
              <div class="fw500 lh13">Laskun numero</div>
              <div class="lh13">
                {{ $route.params.id }}-{{ String($route.params.report_id).padStart(5, '0') }}
              </div>
            </div>
          </div>
        </div>
        <v-divider />
        <div class="grid pa-4 f12">
          <div>
            <div class="fw500 lh13">Laskutusosoite</div>
            <div class="lh13">{{ client.name }}</div>
            <div class="lh13">{{ client.registration_street }}</div>
            <div class="lh13 text-uppercase">
              {{ client.registration_zip }}
              {{ client.registration_city }}
            </div>
          </div>
          <div class="grid lh13">
            <div class="fw500">Asiakasnumero</div>
            <div>{{ client.id }}</div>
            <div class="fw500">Laskun eräpäivä</div>
            <div>{{ addDays(maksuehto) }}</div>
            <div class="fw500">Maksuviite</div>
            <div>
              #{{
                companiesConfig.short_name +
                String(today.yyyy).slice(2, 4) +
                String($route.params.id).padStart(4, '0')
              }}
            </div>
            <div class="fw500 align-self-center">Maksuehto</div>
            <v-select
              dense
              hide-details
              outlined
              v-model="maksuehto"
              :items="[7, 14, 21, 30, 45, 60]"
              :rules="[(v) => !!v || '']"
              class="f12"
              :menu-props="{ bottom: true, offsetY: true }"
            >
              <template v-slot:append-outer>
                <div class="f12">pv netto</div>
              </template>
            </v-select>
            <div class="fw500">Viivästyskorko</div>
            <div>8.5%</div>
            <div class="fw500">Toimittajan puhelinnumero</div>
            <div>{{ companiesConfig.phone || '-' }}</div>
            <div class="fw500">Toimittajan sähköpostiosoite</div>
            <div>{{ companiesConfig.laskut_email || '-' }}</div>
            <div class="fw500">Laskutettavan ALV-tunnus</div>
            <div>{{ companiesConfig.vat || '-' }}</div>
          </div>
        </div>
        <div class="px-4">
          <StatisticProjectReportsBlankMainTable
            :left="
              Array(
                data.data.project.name,
                data.data.project.street +
                  ', ' +
                  data.data.project.zip +
                  ', ' +
                  data.data.project.city,
                $formatDate(data.data.project.start_date) +
                  ' - ' +
                  $formatDate(data.data.project.end_date) +
                  ' Vk ' +
                  selectedDates.weeks,
              )
            "
            :right="
              Array(
                client.name,
                data.data.project.manager.first_name + ' ' + data.data.project.manager.last_name,
                data.data.project.manager.phone,
              )
            "
          />
        </div>
        <div class="px-4">
          <div class="border scrollHorizontal">
            <div class="grid_table_header">
              <div>Pos</div>
              <div>Määrä / Yks</div>
              <div>Työ</div>
              <div>Hinta Alennus</div>
              <div>ALV</div>
              <div>Veroton summa EUR</div>
              <div>Verollinen summa EUR</div>
            </div>
            <div v-for="(work, w) in work_types" :key="'work_types_' + w" class="grid_table fw500">
              <div>{{ w + 1 }}</div>
              <div>
                {{
                  work.is_hourly === 1
                    ? $formatTime(work.done)
                    : work.done + ' ' + work.measure
                }}
              </div>
              <div>{{ work.name }}</div>
              <v-text-field
                dense
                hide-details
                outlined
                type="numeric"
                class="f12 none_border fw500"
                v-model="work.price"
                :rules="[(v) => !!v || '']"
              />
              <v-select
                dense
                hide-details
                outlined
                class="f12 none_border fw500"
                v-model="work.vat"
                @change="table_key++"
                :items="[0, 24]"
                :rules="[(v) => v === 24 || v === 0 || '']"
                :menu-props="{ bottom: true, offsetY: true }"
              >
                <template v-slot:append>
                  <div class="f12 none_border fw500">%</div>
                </template>
              </v-select>
              <div>{{ getVeroton(work.price, work.done, work.is_hourly) }}</div>
              <div>{{ getVerollinen(work.price, work.done, work.is_hourly, work.vat) }}</div>
            </div>
            <div v-if="extras.length > 0">
              <div class="grid_table">
                <div class="fw500">{{ work_types.length + 1 }}</div>
                <div class="fw500">
                  {{
                    $formatTime(
                      Object.values(extras).reduce((sum, current) => {
                        return sum + current.done
                      }, 0),
                    )
                  }}
                </div>
                <div class="fw500">Yhteensä lisätyö</div>
                <div class="fw500">
                  {{
                    extras.every((current) => {
                      return (
                        formatNumberDilimiter(current.price) ===
                          formatNumberDilimiter(extraWorksPrice) && current.vat === vat
                      )
                    })
                      ? formatNumberDilimiter(extraWorksPrice)
                      : ''
                  }}
                </div>
                <div class="fw500">
                  {{
                    extras.every((current) => {
                      return (
                        formatNumberDilimiter(current.price) ===
                          formatNumberDilimiter(extraWorksPrice) && current.vat === vat
                      )
                    })
                      ? vat + ' %'
                      : ''
                  }}
                </div>
                <div class="fw500">
                  {{
                    extras.every((current) => {
                      return (
                        formatNumberDilimiter(current.price) ===
                          formatNumberDilimiter(extraWorksPrice) && current.vat === vat
                      )
                    })
                      ? formatNumberDilimiter(
                          extras.reduce((sum, current) => {
                            return sum + Number(getVeroton(current.price, current.done, 1))
                          }, 0),
                        )
                      : ''
                  }}
                </div>
                <div class="fw500">
                  {{
                    extras.every((current) => {
                      return (
                        formatNumberDilimiter(current.price) ===
                          formatNumberDilimiter(extraWorksPrice) && current.vat === vat
                      )
                    })
                      ? formatNumberDilimiter(
                          extras.reduce((sum, current) => {
                            return (
                              sum +
                              Number(getVerollinen(current.price, current.done, 1, current.vat))
                            )
                          }, 0),
                        )
                      : ''
                  }}
                </div>
              </div>
              <div v-for="(extras, e) in extras" :key="'extras_' + e" class="grid_table">
                <div>{{ work_types.length + 1 + '.' + Number(e + 1) }}</div>
                <div>{{ $formatTime(extras.done) }}</div>
                <div>{{ extras.name }}</div>
                <v-text-field
                  dense
                  hide-details
                  outlined
                  type="numeric"
                  class="f12 none_border"
                  v-model="extras.price"
                  @change="table_key++"
                  :rules="[(v) => !!v || '']"
                />
                <v-select
                  dense
                  hide-details
                  outlined
                  class="f12 none_border"
                  v-model="extras.vat"
                  @change="table_key++"
                  :items="[0, 24]"
                  :rules="[(v) => v === 24 || v === 0 || '']"
                  :menu-props="{ bottom: true, offsetY: true }"
                >
                  <template v-slot:append>
                    <div class="f12 none_border fw500">%</div>
                  </template>
                </v-select>
                <div>{{ getVeroton(extras.price, extras.done, 1) }}</div>
                <div>{{ getVerollinen(extras.price, extras.done, 1, extras.vat) }}</div>
              </div>
            </div>
            <div v-if="additional.length > 0">
              <div
                v-for="(work, a) in additional"
                :key="'additional_' + a"
                class="grid_table fw500"
              >
                <div>
                  {{ Number(work_types.length > 0 ? work_types.length : 0) + a + 1 }}
                </div>
                <div>
                  {{
                    work.is_hourly === 1
                      ? $formatTime(work.value1 || work.value)
                      : work.value1 || work.value + ' ' + work.measure
                  }}
                </div>
                <div>{{ work.price }}€/{{ work.measure }} {{ work.name }}</div>
                <div>{{ work.price }}</div>
                <v-select
                  dense
                  hide-details
                  outlined
                  class="f12 none_border fw500"
                  v-model="work.vat"
                  @change="table_key++"
                  :items="[0, 24]"
                  :rules="[(v) => v === 24 || v === 0 || '']"
                  :menu-props="{ bottom: true, offsetY: true }"
                >
                  <template v-slot:append>
                    <div class="f12 none_border fw500">%</div>
                  </template>
                </v-select>
                <div>{{ getVeroton(work.price, work.value1 || work.value, work.is_hourly) }}</div>
                <div>
                  {{
                    getVerollinen(work.price, work.value1 || work.value, work.is_hourly, work.vat)
                  }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="pa-4 grid">
          <div></div>
          <div>
            <div class="fw500">ALV</div>
            <v-sheet max-width="170">
              <v-select
                dense
                hide-details
                outlined
                class="f12 none_border fw500"
                v-model="vat"
                @change="changeVat()"
                :items="[0, 24]"
                :rules="[(v) => v !== 0 || v !== 24 || '']"
                :menu-props="{ bottom: true, offsetY: true }"
              >
                <template v-slot:append-outer>
                  <div class="f12 none_border fw500">%</div>
                </template>
              </v-select>
            </v-sheet>
          </div>
          <div></div>
          <div>
            <div class="d-flex justify-space-between">
              <div class="fw500 lh13">Veroton summa yhteensä</div>
              <div class="lh13">{{ getSum() ? formatNumberDilimiter(getSum()) + ' EUR' : '' }}</div>
            </div>
            <div class="d-flex justify-space-between">
              <div class="fw500 lh13">Veron määrä yhteensä</div>
              <div class="lh13">
                {{
                  getSumPercent() && getSum()
                    ? formatNumberDilimiter(getSumPercent() - getSum()) + ' EUR'
                    : ''
                }}
              </div>
            </div>
            <div class="d-flex justify-space-between">
              <div class="fw500 lh13">Laskun summa yhteensä</div>
              <div class="fw500 lh13">
                {{ getSumPercent() ? formatNumberDilimiter(getSumPercent()) + ' EUR' : '' }}
              </div>
            </div>
          </div>
        </div>
        <v-divider />
        <div class="pa-4 grid_footer">
          <div>
            <div class="fw500 lh13">{{ companiesConfig.company_name }}</div>
            <div class="lh13">{{ companiesConfig.registration_address_street }}</div>
            <div class="lh13">{{ companiesConfig.registration_address_zip }}, {{ companiesConfig.registration_address_city }}</div>
          </div>
          <div>
            <div class="lh13">Y-tunnus: {{ companiesConfig.company_id }}</div>
            <div class="lh13">ALV-tunnus: {{ companiesConfig.vat }}</div>
            <div class="lh13">Puhelin: {{ companiesConfig.phone }}</div>
            <div class="lh13">Sähköpostiosoite: {{ companiesConfig.laskut_email }}</div>
          </div>
          <div>
            <div class="lh13 fw500">BIC</div>
            <div>{{ companiesConfig.bic }}</div>
          </div>
          <div>
            <div class="lh13 fw500">IBAN</div>
            <div>{{ companiesConfig.iban }}</div>
          </div>
        </div>
      </v-card>
    </v-form>
    <div class="fixedBtn">
      <BaseTooltip>
        <BaseButton
          v-if="pdf_page"
          @click="prepareAll()"
          default_icon="button-create_pdf"
          round
          size="60"
          icon_only
          loading
          custom-class="another"
          class="mb-2"
        />
      </BaseTooltip>
      <BaseTooltip
        :tooltip="
          pdf_page
            ? localStorage.translations.pages.common['buttons']['redaktirovat']
            : localStorage.translations.pages.common['buttons']['sohranit']
        "
      >
        <BaseButton
          @click="onEdit"
          round
          size="60"
          icon_only
          :default_icon="pdf_page ? 'button-edit' : 'button-pdf'"
        />
      </BaseTooltip>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import StatisticProjectReportsBlankMainTable from './StatisticProjectReportsBlankMainTable'
import BaseTooltip from '../../base/BaseTooltip'
import BaseButton from '../../base/BaseButton'
import PdfGen from '../../pdf/pdfGen'

export default {
  name: 'StatisticProjectReportCheckBlank',
  components: {
    PdfGen,
    BaseButton,
    BaseTooltip,
    StatisticProjectReportsBlankMainTable,
  },
  props: {
    data: Object,
    selectedDates: Object,
    client: Object,
    work: Array,
    extraWorksPrice: [Number, String],
  },
  computed: {
    ...mapGetters(['companiesConfig', 'apiDomain']),
    pdf_page() {
      return this.$route.name === 'timeControlShowReportsViewCheckPdf'
    },
    today() {
      let today = new Date()
      let dd = String(today.getDate()).padStart(2, '0')
      let mm = String(today.getMonth() + 1).padStart(2, '0')
      let yyyy = today.getFullYear()
      return {
        today: today,
        dd: dd,
        mm: mm,
        yyyy: yyyy,
      }
    },
    ttl() {
      let result = {
        work_types: [],
        extras: [],
      }
      if (this.data) {
        if (this.data.additional_data) {
          let items = this.data.additional_data.employees
          let arr = Object.keys(result)
          for (let r in arr) {
            let name = arr[r]
            let work = {}
            let all = []
            for (let i in items) {
              all = all.concat(Object.values(items[i].days))
            }
            let temp = all.filter((i) => i[name]).map((data) => data[name]) || []
            for (let t in temp) {
              for (let key in temp[t]) {
                if (!work[key]) {
                  work[key] = []
                }
                work[key].push(temp[t][key])
              }
            }
            for (let w in work) {
              if (work[w] && work[w][0]) {
                result[name].push({
                  key: w,
                  is_hourly: work[w][0].is_hourly,
                  measure: work[w][0].measure,
                  name: work[w][0].name,
                  value: work[w].reduce((sum, current) => {
                    return sum + current.value
                  }, 0),
                  value1: work[w].reduce((sum, current) => {
                    return current.cnf &&
                      current.cnf === true &&
                      current.show &&
                      current.show === true
                      ? sum + Number(current.value1)
                      : sum
                  }, 0),
                })
              }
            }
          }
        }
      }
      console.log(result)
      return result
    },
    work_types() {
      let temp = this.ttl.work_types
      temp.forEach((current) => {
        current.price = this.formatNumberDilimiter(
          this.work.filter((i) => i.work_type && Number(i.work_type.id) === Number(current.key))[0]
            .price,
        )
        current.vat = 0
        current.done = current.value1
      })
      return temp.filter((i) => i.value1)
    },
    extras() {
      let temp = this.ttl.extras
      temp.forEach((current) => {
        current.price = this.formatNumberDilimiter(this.extraWorksPrice)
        current.vat = 0
        current.done = current.value1
      })
      return temp.filter((i) => i.value1)
    },
    additional() {
      let temp = []
      if (this.data && this.data.additional_data) {
        temp = this.data.additional_data.additional
      }
      temp.forEach((current) => {
        current.vat = 0
        current.price = this.formatNumberDilimiter(current.price)
      })
      return temp
    },
    pdf_header() {
      return {
        style: 'h9',
        columns: [
          {
            table: {
              widths: ['*', 130, 110],
              body: [
                [
                  [
                    {
                      image: 'logo',
                      width: 50,
                      height: 50,
                      margin: [15, 10, 10, 10],
                    },
                    {
                      text: this.companiesConfig.company_name,
                      margin: [15, 0, 10, 0],
                    },
                    {
                      text:
                        this.companiesConfig.registration_address_street +
                        ', ' +
                        this.companiesConfig.registration_address_zip +
                        ', ' +
                        this.companiesConfig.registration_address_city,
                      margin: [15, 0, 10, 5],
                    },
                  ],
                  [
                    {
                      text: 'Lasku',
                      style: 'black',
                      margin: [0, 30, 0, 3],
                    },
                    {
                      text: 'Päivämäärä',
                      style: 'h9bold',
                    },
                    { text: this.today.dd + '.' + this.today.mm + '.' + this.today.yyyy },
                  ],
                  [
                    {
                      text: 'Laskun numero',
                      style: 'h9',
                      margin: [0, 35, 0, 0],
                    },
                    {
                      text:
                        this.$route.params.id +
                        '-' +
                        String(this.$route.params.report_id).padStart(5, '0'),
                    },
                  ],
                ],
              ],
            },
            layout: {
              hLineColor: '#d2d5d9',
              vLineWidth: function (i) {
                return 0
              },
            },
          },
        ],
      }
    },
    pdf_footer() {
      return {
        style: 'h9',
        columns: [
          {
            table: {
              widths: [130, '*', 100, 130],
              body: [
                [
                  [
                    {
                      text: this.companiesConfig.company_name,
                      style: 'h9bold',
                      margin: [15, 5, 0, 0],
                    },
                    {
                      text: this.companiesConfig.registration_address_street,
                      margin: [15, 0, 0, 0],
                    },
                    {
                      text: this.companiesConfig.registration_address_zip + ', ' + this.companiesConfig.registration_address_city,
                      margin: [15, 0, 0, 10],
                    },
                  ],
                  [
                    {
                      text: 'Y-tunnus: ' + String(this.companiesConfig.company_id || ''),
                      margin: [0, 5, 0, 0],
                    },
                    { text: 'ALV-tunnus: ' + String(this.companiesConfig.vat || '') },
                    { text: 'Puhelin: ' + String(this.companiesConfig.phone || '') },
                    {
                      text: 'Sähköpostiosoite: ' + String(this.companiesConfig.laskut_email || ''),
                      margin: [0, 0, 0, 10],
                    },
                  ],
                  [
                    { text: 'BIC', style: 'h9bold', margin: [0, 5, 0, 0] },
                    { text: this.companiesConfig.bic || '' },
                  ],
                  [
                    { text: 'IBAN', style: 'h9bold', margin: [0, 5, 15, 0] },
                    { text: this.companiesConfig.iban || '', margin: [0, 0, 15, 0] },
                  ],
                ],
              ],
            },
            layout: {
              hLineColor: '#d2d5d9',
              vLineColor: 'white',
            },
          },
        ],
      }
    },
    pdf_content() {
      let temp = [
        {
          style: 'h9',
          columns: [
            {
              width: '*',
              columns: [
                [
                  { text: 'Laskutusosoite', style: 'h9bold' },
                  { text: this.client.name || '' },
                  { text: this.client.registration_street || '' },
                  {
                    text:
                      String(this.client.registration_zip || '') +
                      ' ' +
                      String(this.client.registration_city || ''),
                  },
                ],
              ],
            },
            {
              width: 138,
              columns: [
                [
                  { text: 'Asiakasnumero', style: 'h9bold' },
                  { text: 'Laskun eräpäivä', style: 'h9bold' },
                  { text: 'Maksuviite', style: 'h9bold' },
                  { text: 'Maksuehto', style: 'h9bold' },
                  { text: 'Viivästyskorko', style: 'h9bold' },
                  { text: 'Toimittajan puhelinnumero', style: 'h9bold' },
                  { text: 'Toimittajan sähköpostiosoite', style: 'h9bold' },
                  { text: 'Laskutettavan ALV-tunnus', style: 'h9bold' },
                ],
              ],
            },
            {
              width: 100,
              columns: [
                [
                  { text: this.client.id || '-' },
                  { text: this.addDays(this.maksuehto) || '' },
                  {
                    text:
                      '#' +
                      this.companiesConfig.short_name +
                      String(this.today.yyyy).slice(2, 4) +
                      String(this.$route.params.id).padStart(4, '0'),
                  },
                  { text: this.maksuehto + ' pv netto' },
                  { text: '8.5%' },
                  { text: this.companiesConfig.phone || '' },
                  { text: this.companiesConfig.laskut_email || '' },
                  { text: this.companiesConfig.vat || '' },
                ],
              ],
            },
          ],
        },
        {
          style: 'h9',
          margin: [0, 10, 0, 10],
          table: {
            widths: ['*', '*'],
            body: [
              [
                {
                  table: {
                    widths: [70, '*'],
                    body: [
                      [
                        { text: 'Projektinimi', style: 'mt' },
                        { text: this.data.data.project.name || '', style: 'mt' },
                      ],
                      [
                        { text: 'Osoite', style: 'mt' },
                        {
                          text:
                            this.data.data.project.street +
                            ', ' +
                            this.data.data.project.zip +
                            ', ' +
                            this.data.data.project.city,
                          style: 'mt',
                        },
                      ],
                      [
                        { text: 'Kausi', style: 'mt' },
                        {
                          text:
                            this.$formatDate(this.data.data.project.start_date) +
                            ' - ' +
                            this.$formatDate(this.data.data.project.end_date) +
                            ' Vk ' +
                            this.selectedDates.weeks,
                          style: 'mt',
                        },
                      ],
                    ],
                  },
                  border: [false, false, true, false],
                  layout: {
                    hLineColor: '#d2d5d9',
                    vLineWidth: function () {
                      return 0
                    },
                    hLineWidth: function (i) {
                      return i !== 0 ? 1 : 0
                    },
                    paddingTop: function () {
                      return 0
                    },
                    paddingBottom: function () {
                      return 0
                    },
                  },
                },
                {
                  table: {
                    widths: [70, '*'],
                    body: [
                      [
                        { text: 'Asiakas', style: 'mt' },
                        { text: this.client.name || '', style: 'mt' },
                      ],
                      [
                        { text: 'Tilaaja', style: 'mt' },
                        {
                          text:
                            String(this.data.data.project.manager.first_name || '') +
                            ' ' +
                            String(this.data.data.project.manager.last_name || ''),
                          style: 'mt',
                        },
                      ],
                      [
                        { text: 'Puh.:', style: 'mt' },
                        {
                          text: this.data.data.project.manager.phone || '',
                          style: 'mt',
                        },
                      ],
                    ],
                  },
                  border: [true, false, false, false],
                  layout: {
                    hLineColor: '#d2d5d9',
                    vLineWidth: function () {
                      return 0
                    },
                    hLineWidth: function (i) {
                      return i !== 0 ? 1 : 0
                    },
                    paddingTop: function () {
                      return 0
                    },
                    paddingBottom: function () {
                      return 0
                    },
                  },
                },
              ],
            ],
          },
          layout: {
            vLineColor: '#d2d5d9',
            paddingLeft: function (i) {
              return i === 0 ? 0 : 4
            },
            paddingRight: function (i) {
              return i === 0 ? 4 : 0
            },
          },
        },
        {
          style: 'h9',
          table: {
            headerRows: 1,
            widths: [20, 55, '*', 40, 30, 50, 50],
            body: [
              [
                { text: 'Pos', alignment: 'center', margin: [0, 11, 0, 0] },
                { text: 'Määrä / Yks', alignment: 'center', margin: [0, 11, 0, 0] },
                { text: 'Työ', margin: [0, 11, 0, 0] },
                { text: 'Hinta Alennus', alignment: 'center', margin: [0, 5, 0, 0] },
                { text: 'ALV', alignment: 'center', margin: [0, 11, 0, 0] },
                { text: 'Veroton summa EUR', alignment: 'center' },
                { text: 'Verollinen summa EUR', alignment: 'center' },
              ],
            ],
          },
          layout: {
            vLineColor: '#ebebeb',
            hLineColor: '#ebebeb',
            paddingTop: function (i, node) {
              return 6
            },
            paddingBottom: function (i, node) {
              return 2
            },
            fillColor: function (rowIndex, node, columnIndex) {
              return rowIndex === 0 ? '#f2f2f2' : null
            },
          },
        },
        {
          style: 'h9',
          margin: [0, 10, 0, 10],
          columns: [
            { width: '*', text: '' },
            {
              width: 138,
              columns: [
                [
                  { text: 'Veroton summa yhteensä', style: 'h9bold' },
                  { text: 'Veron määrä yhteensä', style: 'h9bold' },
                  { text: 'Laskun summa yhteensä', style: 'h9bold' },
                ],
              ],
            },
            {
              width: 100,
              columns: [
                [
                  {
                    text: this.getSum() ? this.formatNumberDilimiter(this.getSum()) + ' EUR' : '',
                    alignment: 'right',
                  },
                  {
                    text:
                      this.getSumPercent() && this.getSum()
                        ? this.formatNumberDilimiter(this.getSumPercent() - this.getSum()) + ' EUR'
                        : '',
                    alignment: 'right',
                  },
                  {
                    text: this.getSumPercent()
                      ? this.formatNumberDilimiter(this.getSumPercent()) + ' EUR'
                      : '',
                    alignment: 'right',
                  },
                ],
              ],
            },
          ],
        },
      ]
      let work = this.work_types
      for (let i in work) {
        temp[2].table.body.push([
          { text: Number(i) + 1, style: 'h9bold' },
          {
            text: [
              {
                text:
                  work[i].is_hourly === 0
                    ? work[i].value1 || work[i].value
                    : this.$formatTime(work[i].done),
                style: 'h9bold',
              },
              {
                text: work[i].is_hourly === 0 ? work[i].done + ' ' + work[i].measure : '',
                style: 'roboto-black',
              },
            ],
            margin: work[i].is_hourly === 0 ? [0, -2, 0, -2] : [0, 0, 0, 0],
          },
          { text: work[i].name, style: 'h9bold' },
          { text: work[i].price, style: 'h9bold', alignment: 'center' },
          {
            text: String(work[i].vat === 0 || work[i].vat === 24 ? work[i].vat : '-') + ' %',
            style: 'h9bold',
            alignment: 'center',
          },
          {
            text: this.getVeroton(work[i].price, work[i].done, work[i].is_hourly) || '-',
            style: 'h9bold',
            alignment: 'center',
          },
          {
            text:
              this.getVerollinen(work[i].price, work[i].done, work[i].is_hourly, work[i].vat) ||
              '-',
            style: 'h9bold',
            alignment: 'center',
          },
        ])
      }
      if (this.extras.length > 0) {
        temp[2].table.body.push([
          { text: this.work_types.length + 1, style: 'h9bold' },
          {
            text:
              this.$formatTime(
                this.extras.reduce((sum, current) => {
                  return sum + current.done
                }, 0),
              ) || '-',
            style: 'h9bold',
          },
          { text: 'Yhteensä lisätyö', style: 'h9bold' },
          {
            text: this.extras.every((current) => {
              return (
                this.formatNumberDilimiter(current.price) ===
                  this.formatNumberDilimiter(this.extraWorksPrice) && current.vat === this.vat
              )
            })
              ? this.formatNumberDilimiter(this.extraWorksPrice)
              : '',
            style: 'h9bold',
            alignment: 'center',
          },
          {
            text: this.extras.every((current) => {
              return (
                this.formatNumberDilimiter(current.price) ===
                  this.formatNumberDilimiter(this.extraWorksPrice) && current.vat === this.vat
              )
            })
              ? this.vat + ' %'
              : '',
            style: 'h9bold',
            alignment: 'center',
          },
          {
            text: this.extras.every((current) => {
              return (
                this.formatNumberDilimiter(current.price) ===
                  this.formatNumberDilimiter(this.extraWorksPrice) && current.vat === this.vat
              )
            })
              ? this.formatNumberDilimiter(
                  this.extras.reduce((sum, current) => {
                    return sum + Number(this.getVeroton(current.price, current.done, 1))
                  }, 0),
                )
              : '',
            style: 'h9bold',
            alignment: 'center',
          },
          {
            text: this.extras.every((current) => {
              return (
                this.formatNumberDilimiter(current.price) ===
                  this.formatNumberDilimiter(this.extraWorksPrice) && current.vat === this.vat
              )
            })
              ? this.formatNumberDilimiter(
                  this.extras.reduce((sum, current) => {
                    return (
                      sum + Number(this.getVerollinen(current.price, current.done, 1, current.vat))
                    )
                  }, 0),
                )
              : '',
            style: 'h9bold',
            alignment: 'center',
          },
        ])
        let extras = this.extras
        for (let i in extras) {
          temp[2].table.body.push([
            Object.values(this.data.data.work_types).length + 1 + '.' + Number(Number(i) + 1),
            this.$formatTime(extras[i].done) || '-',
            extras[i].name || '-',
            { text: extras[i].price || '-', alignment: 'center' },
            {
              text:
                String(extras[i].vat === 0 || extras[i].vat === 24 ? extras[i].vat : '-') + ' %',
              alignment: 'center',
            },
            {
              text: this.getVeroton(extras[i].price, extras[i].done, 1) || '-',
              alignment: 'center',
            },
            {
              text: this.getVerollinen(extras[i].price, extras[i].done, 1, extras[i].vat) || '-',
              alignment: 'center',
            },
          ])
        }
      }
      for (let i in this.additional) {
        temp[2].table.body.push([
          { text: this.work_types.length + Number(i) + 1, style: 'h9bold' },
          {
            text: [
              {
                text:
                  this.additional[i].is_hourly === 0
                    ? this.additional[i].value
                    : this.$formatTime(this.additional[i].value),
                style: 'h9bold',
              },
              {
                text: this.additional[i].is_hourly === 0 ? ' ' + this.additional[i].measure : '',
                style: 'roboto-black',
              },
            ],
            margin: this.additional[i].is_hourly === 0 ? [0, -2, 0, -2] : [0, 0, 0, 0],
          },
          { text: this.additional[i].name, style: 'h9bold' },
          { text: this.additional[i].price, style: 'h9bold', alignment: 'center' },
          {
            text:
              String(
                this.additional[i].vat === 0 || this.additional[i].vat === 24
                  ? this.additional[i].vat
                  : '-',
              ) + ' %',
            style: 'h9bold',
            alignment: 'center',
          },
          {
            text:
              this.getVeroton(
                this.additional[i].price,
                this.additional[i].value,
                this.additional[i].is_hourly,
              ) || '-',
            style: 'h9bold',
            alignment: 'center',
          },
          {
            text:
              this.getVerollinen(
                this.additional[i].price,
                this.additional[i].value,
                this.additional[i].is_hourly,
                this.additional[i].vat,
              ) || '-',
            style: 'h9bold',
            alignment: 'center',
          },
        ])
      }
      return temp
    },
  },
  data: () => ({
    documents: [],
    maksuehto: 7,
    vat: 0,
    table_key: 0,
  }),
  methods: {
    ...mapActions(['getFromLink']),
    onEdit() {
      if (this.$route.name === 'timeControlShowReportsViewCheck') {
        if (this.$refs.form.validate()) {
          this.$router.push({ name: 'timeControlShowReportsViewCheckPdf' })
        }
      } else {
        this.$router.push({ name: 'timeControlShowReportsViewCheck' })
      }
    },
    addDays(days) {
      let date = new Date()
      date.setDate(date.getDate() + days)
      let dd = String(date.getDate()).padStart(2, '0')
      let mm = String(date.getMonth() + 1).padStart(2, '0')
      let yyyy = date.getFullYear()
      return dd + '.' + mm + '.' + yyyy
    },
    changeVat() {
      for (let i in this.work_types) {
        this.work_types[i].vat = this.vat
      }
      for (let i in this.extras) {
        this.extras[i].vat = this.vat
      }
      for (let i in this.additional) {
        this.additional[i].vat = this.vat
      }
    },
    getVeroton(price, value, is_hourly) {
      price = Number(price)
      value = Number(value)
      return price
        ? is_hourly === 1
          ? this.formatNumberDilimiter((price * (value / 0.6)) / 100)
          : this.formatNumberDilimiter(price * value)
        : null
    },
    getVerollinen(price, value, is_hourly, percent) {
      return percent === 0 || percent === 24
        ? this.formatNumberDilimiter(
            (this.getVeroton(price, value, is_hourly) * (Number(percent) + 100)) / 100,
          )
        : null
    },
    getSum() {
      let result = 0
      for (let i in this.work_types) {
        let line = this.work_types[i]
        result += Number(this.getVeroton(line.price, line.done, line.is_hourly))
      }
      for (let i in this.extras) {
        let line = this.extras[i]
        result += Number(this.getVeroton(line.price, line.done, 1))
      }
      for (let i in this.additional) {
        let line = this.additional[i]
        result += Number(this.getVeroton(line.price, line.value, line.is_hourly))
      }
      return result > 0 ? result : null
    },
    getSumPercent() {
      let result = 0
      for (let i in this.work_types) {
        let line = this.work_types[i]
        result += Number(this.getVerollinen(line.price, line.done, line.is_hourly, line.vat))
      }
      for (let i in this.extras) {
        let line = this.extras[i]
        result += Number(this.getVerollinen(line.price, line.done, 1, line.vat))
      }
      for (let i in this.additional) {
        let line = this.additional[i]
        result += Number(this.getVerollinen(line.price, line.value, line.is_hourly, line.vat))
      }
      return result > 0 ? result : null
    },
    async prepareAll() {
      if (this.$refs.invoice) {
        this.$refs.invoice.onDownload()
      }
    },
    downloadBlob(data, fileName, mimeType) {
      const blob = new Blob([data], {
        type: 'application/pdf',
      })
      const url = window.URL.createObjectURL(blob)
      this.downloadURL(url, fileName)
    },
    downloadURL(data, fileName) {
      const a = document.createElement('a')
      a.href = data
      a.download = fileName
      document.body.appendChild(a)
      a.style.display = 'none'
      a.click()
      a.remove()
    },
    formatNumberDilimiter(num) {
      return (Math.round(num * 100) / 100).toFixed(2)
    },
  },
}
</script>

<style scoped lang="scss">
.grid {
  display: grid;
  grid-template-rows: auto;
  grid-template-columns: 1.2fr 1fr;
  grid-gap: 5px;
  @media #{map-get($display-breakpoints, 'sm-and-down')} {
    grid-template-columns: 1fr;
  }

  &_total {
    @extend .grid;
    grid-template-columns: 1fr 1fr;
  }

  &_footer {
    @extend .grid;
    grid-gap: 10px;
    grid-template-columns: 1.5fr auto 1fr 1.5fr;
    @media #{map-get($display-breakpoints, 'sm-and-down')} {
      grid-template-columns: 1fr;
    }
  }

  &_head {
    @extend .grid;
    //grid-template-columns: 1.7fr 1fr;
    align-content: center;
    align-items: center;
    @media #{map-get($display-breakpoints, 'sm-and-down')} {
      grid-template-columns: 1.2fr 1fr;
    }
  }

  &_info {
    @extend .grid;
    grid-template-columns: auto 1fr;
  }

  &_table {
    @extend .grid;
    grid-template-columns: 30px 90px minmax(300px, 1fr) 65px 65px 65px 65px;
    grid-gap: 0;

    .v-text-field {
      margin: 0 !important;
      padding: 0 !important;
    }

    :deep() .v-text-field input {
      padding: 4px 0 !important;
    }

    :deep() .v-text-field .v-input__control {
      border-radius: 0 !important;
      height: 100% !important;
    }

    :deep() .v-input .v-input__slot:not(.v-textarea) fieldset {
      color: #ebebeb !important;
    }

    :deep()
      .v-text-field.v-text-field--enclosed:not(.v-text-field--rounded)
      > .v-input__control
      > .v-input__slot {
      padding: 0 4px !important;

      .v-input__append-inner {
        //margin-top: 6px !important;
      }
    }

    & div {
      padding: 4px;
      border: 1px solid #ebebeb;
      display: flex;
      align-items: center;
      min-height: 28px;
    }

    &_header {
      @extend .grid_table;
      background-color: #f2f2f2;
    }
  }
}

.border {
  border: 1px solid #ebebeb;
}

.none_border {
  border: none !important;
}

.lh13 {
  line-height: 1.3em !important;
}

:deep() .v-input.v-text-field .v-input__slot:not(.v-textarea) {
  min-height: 16px !important;
}

:deep()
  .v-select.v-text-field--outlined:not(.v-text-field--single-line).v-input--dense
  .v-select__selections {
  padding: 0 !important;
}

:deep() .v-input .v-input__append-outer {
  //margin-top: 7px !important;
  width: 70%;
}

.fixedBtn {
  z-index: 10;
  position: fixed;
  bottom: 165px;
  right: 10px;

  @media #{map-get($display-breakpoints, 'sm-and-down')} {
    padding: 15px;
    bottom: 135px;
    right: -2px;
    :deep() button {
      width: 45px !important;
      height: 45px !important;
      padding: 10px !important;
    }
  }
}

.scrollHorizontal {
  //max-width: 85vw;
  @media #{map-get($display-breakpoints, 'sm-and-down')} {
    overflow-x: scroll;
  }
}
</style>
